import { api } from '@/scripts/api'

/**
 * 工作流应用历史分组接口
 */
export interface WorkflowAppHistoryGroup {
  id: number
  name: string
  sort_order: number
  created_at?: string
  updated_at?: string
}

/**
 * 工作流应用历史分组服务
 * 用于管理用户的工作流应用历史分组
 */
export class WorkflowAppGroupService {
  private cachedGroups: WorkflowAppHistoryGroup[] = []
  private cacheTimestamp = 0
  private readonly cacheTTL = 60 * 1000 // 60秒缓存

  private shouldUseCache(forceRefresh: boolean) {
    if (forceRefresh) return false
    return (
      this.cachedGroups.length >= 0 &&
      Date.now() - this.cacheTimestamp < this.cacheTTL
    )
  }

  private setCache(groups: WorkflowAppHistoryGroup[]) {
    this.cachedGroups = groups
    this.cacheTimestamp = Date.now()
  }

  private invalidateCache() {
    this.cachedGroups = []
    this.cacheTimestamp = 0
  }

  /**
   * 获取用户的所有分组
   * @returns Promise<WorkflowAppHistoryGroup[]>
   */
  async getGroups(forceRefresh = false): Promise<WorkflowAppHistoryGroup[]> {
    if (this.shouldUseCache(forceRefresh)) {
      return this.cachedGroups
    }

    try {
      const response = await api.fetchApi('/workflow-apps/history/groups')
      if (response.ok) {
        const data = await response.json()
        const groups: WorkflowAppHistoryGroup[] = data.groups || []
        this.setCache(groups)
        return groups
      }
      return []
    } catch (error) {
      console.error('获取分组列表失败:', error)
      return []
    }
  }

  /**
   * 创建新分组
   * @param name 分组名称
   * @returns Promise<WorkflowAppHistoryGroup | null>
   */
  async createGroup(name: string): Promise<WorkflowAppHistoryGroup | null> {
    try {
      const response = await api.fetchApi('/workflow-apps/history/groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name })
      })
      if (response.ok) {
        const data = await response.json()
        this.invalidateCache()
        return data.group || null
      }
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || '创建分组失败')
    } catch (error) {
      console.error('创建分组失败:', error)
      throw error
    }
  }

  /**
   * 更新分组信息
   * @param groupId 分组ID
   * @param data 更新数据
   * @returns Promise<WorkflowAppHistoryGroup | null>
   */
  async updateGroup(
    groupId: number,
    data: { name?: string; sort_order?: number }
  ): Promise<WorkflowAppHistoryGroup | null> {
    try {
      const response = await api.fetchApi(
        `/workflow-apps/history/groups/${groupId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        }
      )
      if (response.ok) {
        const result = await response.json()
        this.invalidateCache()
        return result.group || null
      }
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || '更新分组失败')
    } catch (error) {
      console.error('更新分组失败:', error)
      throw error
    }
  }

  /**
   * 删除分组
   * @param groupId 分组ID
   * @returns Promise<boolean>
   */
  async deleteGroup(groupId: number): Promise<boolean> {
    try {
      const response = await api.fetchApi(
        `/workflow-apps/history/groups/${groupId}`,
        {
          method: 'DELETE'
        }
      )
      if (response.ok) {
        this.invalidateCache()
        return true
      }
      return false
    } catch (error) {
      console.error('删除分组失败:', error)
      return false
    }
  }

  /**
   * 更新历史记录的分组归属
   * @param historyId 历史记录ID
   * @param groupId 分组ID（null表示移出分组）
   * @returns Promise<boolean>
   */
  async updateHistoryGroup(
    historyId: number,
    groupId: number | null
  ): Promise<boolean> {
    try {
      const response = await api.fetchApi(
        `/workflow-apps/history/${historyId}/group`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ group_id: groupId })
        }
      )
      return response.ok
    } catch (error) {
      console.error('更新历史记录分组失败:', error)
      return false
    }
  }
}

export const workflowAppGroupService = new WorkflowAppGroupService()

export function useWorkflowAppGroupService(): WorkflowAppGroupService {
  return workflowAppGroupService
}
